import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import moment from "moment";
import Swal from "sweetalert2";
import { saveAs } from "file-saver";
import api from "../../../components/services/api";
import {
  CONTAINER_NO_MAX_LENGTH,
  containerNoWarning,
  emptyTripRow,
  errorText,
  isBlankRow,
  normalizeContainerNo,
  toTripPayload,
  toTripRow,
} from "./truckTripUtils";

const formatSchedule = (value) => (value ? moment(value).format("DD-MM-YYYY HH:mm") : "-");

function containerFieldProps(value) {
  const warning = containerNoWarning(value);
  if (!warning) return {};
  return { helperText: warning, FormHelperTextProps: { sx: { color: "warning.main" } } };
}

// ถามว่าจะแทนที่หรือต่อท้าย เมื่อในตารางมีข้อมูลอยู่แล้ว ; null = ยกเลิก
async function chooseImportMode(existingCount, importedCount) {
  if (existingCount === 0) return "replace";
  const result = await Swal.fire({
    icon: "question",
    text: `ในตารางมี ${existingCount} เที่ยวอยู่แล้ว จะใช้ ${importedCount} เที่ยวจากไฟล์อย่างไร?`,
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: "แทนที่ของเดิม",
    denyButtonText: "ต่อท้าย",
    cancelButtonText: "ยกเลิก",
  });
  if (result.isConfirmed) return "replace";
  if (result.isDenied) return "append";
  return null;
}

function TripRow({ row, index, canEdit, datalistId, onChange, onRemove }) {
  return (
    <TableRow>
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={row.TruckNo}
          disabled={!canEdit}
          inputProps={{ list: datalistId, maxLength: 50 }}
          onChange={(e) => onChange(row.key, "TruckNo", e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={row.ContainerNo}
          disabled={!canEdit}
          inputProps={{ maxLength: CONTAINER_NO_MAX_LENGTH }}
          onChange={(e) => onChange(row.key, "ContainerNo", e.target.value)}
          onBlur={(e) => onChange(row.key, "ContainerNo", normalizeContainerNo(e.target.value))}
          {...containerFieldProps(row.ContainerNo)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          type="datetime-local"
          value={row.ETD}
          disabled={!canEdit}
          onChange={(e) => onChange(row.key, "ETD", e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          type="datetime-local"
          value={row.ETA}
          disabled={!canEdit}
          onChange={(e) => onChange(row.key, "ETA", e.target.value)}
        />
      </TableCell>
      <TableCell>
        <IconButton aria-label="ลบแถว" color="error" disabled={!canEdit} onClick={() => onRemove(row.key)}>
          <DeleteOutlineIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default function TruckTripTable({ legKey, leg, canEdit, truckNos, onSaved, onDirtyChange }) {
  const [rows, setRows] = useState(() => leg.Trips.map((trip) => toTripRow(trip)));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const datalistId = `truck-nos-${legKey}`;

  // ข้อมูลที่บันทึกแล้วเปลี่ยน (โหลดใหม่ / หลังบันทึก) -> ล้างการแก้ไขในตาราง
  useEffect(() => {
    setRows(leg.Trips.map((trip) => toTripRow(trip)));
    setDirty(false);
  }, [leg.Trips]);

  useEffect(() => {
    onDirtyChange(legKey, dirty);
  }, [legKey, dirty, onDirtyChange]);

  const editRows = (update) => {
    setRows(update);
    setDirty(true);
    setMessage(null);
  };

  const handleChange = (key, field, value) =>
    editRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const handleRemove = (key) => editRows((prev) => prev.filter((row) => row.key !== key));

  const handleAdd = () => editRows((prev) => [...prev, emptyTripRow(leg)]);

  const handleReset = () => {
    setRows(leg.Trips.map((trip) => toTripRow(trip)));
    setDirty(false);
    setMessage(null);
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await api.post("api/truck/trip/save", {
        VoyageScheduleId: leg.VoyageScheduleId,
        Trips: rows.map((row) => toTripPayload(row)),
      });
      onSaved(legKey, res.data.Trips);
      setMessage({ severity: "success", text: `บันทึกแล้ว ${res.data.Count} เที่ยว` });
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "บันทึกไม่สำเร็จ") });
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = null;
    if (!file) return;

    const formData = new FormData();
    formData.append("fileUpload", file);
    setBusy(true);
    try {
      const res = await api.post("api/truck/trip/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const imported = res.data.Trips.map((trip) => toTripRow(trip));
      const current = rows.filter((row) => !isBlankRow(row));
      const mode = await chooseImportMode(current.length, imported.length);
      if (mode === null) return;
      editRows(mode === "replace" ? imported : [...current, ...imported]);
      setMessage({ severity: "info", text: `นำเข้า ${imported.length} เที่ยวจากไฟล์แล้ว — ตรวจสอบแล้วกด "บันทึก"` });
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "นำเข้าไฟล์ไม่สำเร็จ") });
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`api/truck/trip/export/${leg.VoyageScheduleId}`, { responseType: "blob" });
      saveAs(res.data, `truck-trips-${leg.VoyNo}.xlsx`);
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "Export ไม่สำเร็จ") });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, marginBottom: 2 }}>
        <Typography sx={{ flexGrow: 1 }}>
          <b>{leg.VoyNo}</b> {leg.LocFrom} → {leg.LocTo} · ETD {formatSchedule(leg.ETD)} · ETA {formatSchedule(leg.ETA)}
        </Typography>
        {canEdit && (
          <>
            <Button startIcon={<AddIcon />} onClick={handleAdd} disabled={busy}>
              เพิ่มเที่ยว
            </Button>
            <Button component="label" startIcon={<UploadFileIcon />} disabled={busy}>
              Import Excel
              <input hidden type="file" accept=".xlsx" onChange={handleImport} />
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          style={{ color: "#84BD5A", borderColor: "#84BD5A" }}
          onClick={handleExport}
          disabled={dirty}
        >
          Export Excel
        </Button>
        {canEdit && (
          <>
            <Button variant="outlined" onClick={handleReset} disabled={!dirty || busy}>
              ยกเลิกการแก้ไข
            </Button>
            <Button variant="contained" style={{ background: "#80c340" }} onClick={handleSave} disabled={!dirty || busy}>
              {busy ? <CircularProgress size={20} color="inherit" /> : "บันทึก"}
            </Button>
          </>
        )}
      </Box>

      {dirty && (
        <Typography variant="body2" sx={{ color: "warning.main", marginBottom: 1 }}>
          มีการแก้ไขที่ยังไม่บันทึก (Export ได้หลังกดบันทึก)
        </Typography>
      )}
      {message && (
        <Alert severity={message.severity} sx={{ marginBottom: 2, whiteSpace: "pre-line" }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <datalist id={datalistId}>
        {truckNos.map((truckNo) => (
          <option key={truckNo} value={truckNo} />
        ))}
      </datalist>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50}>No</TableCell>
              <TableCell width="20%">เลขรถ</TableCell>
              <TableCell width="22%">เลขตู้</TableCell>
              <TableCell>ETD</TableCell>
              <TableCell>ETA</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TripRow
                key={row.key}
                row={row}
                index={index}
                canEdit={canEdit}
                datalistId={datalistId}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", padding: 4 }}>
                  ยังไม่มีเที่ยวรถใน VOY นี้
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
